import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useIsDarkTheme, useTheme } from '../../hooks/useTheme';
import type { UserRole } from '../../types';

const ROLE_LABELS: Record<UserRole, string> = {
  traveler: 'Traveler',
  host: 'Host',
  admin: 'Admin',
};

const ROLE_HOME: Record<UserRole, string> = {
  traveler: '/home',
  host: '/host',
  admin: '/admin',
};

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`w-3 h-3 ml-auto text-app-muted transition-transform ${className ?? ''}`}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function Check() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-accent">
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
    </svg>
  );
}

interface AccountMenuProps {
  /** Desktop icon-only mode (used by the collapsible app sidebar). */
  collapsed?: boolean;
  /** 'quiet' renders the design-system user-footer trigger (30px avatar,
   *  hairline top border handled by the parent). Default 'default'. */
  variant?: 'default' | 'quiet';
  /** Called after navigating away — e.g. to close a mobile drawer. */
  onNavigate?: () => void;
}

/**
 * Self-contained account popover: profile settings, role switch, theme, logout.
 * Wires its own auth/theme hooks so any sidebar can drop it in.
 */
export function AccountMenu({ collapsed, variant = 'default', onNavigate }: AccountMenuProps) {
  const { profile, activeRole, roles, switchRole, signOut } = useAuth();
  // `setTheme` is a stateless action — safe to call from this instance.
  // Which option reads as "current", though, must NOT come from this
  // instance's own `theme` state: another useTheme() instance elsewhere in
  // the tree (e.g. the sidebar header's own toggle icon) can change the
  // theme without this one ever hearing about it — see the staleness note
  // on useIsDarkTheme in useTheme.ts.
  const { setTheme } = useTheme();
  const isDarkTheme = useIsDarkTheme();
  const currentTheme = isDarkTheme ? 'dark' : 'light';
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [roleSubmenu, setRoleSubmenu] = useState(false);
  const [themeSubmenu, setThemeSubmenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setRoleSubmenu(false);
        setThemeSubmenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function closeAll() {
    setOpen(false);
    setRoleSubmenu(false);
    setThemeSubmenu(false);
  }

  async function handleSwitch(role: UserRole) {
    closeAll();
    await switchRole(role);
    navigate(ROLE_HOME[role]);
    onNavigate?.();
  }

  function handleSettings() {
    closeAll();
    navigate('/settings');
    onNavigate?.();
  }

  async function handleSignOut() {
    closeAll();
    await signOut();
    navigate('/login');
  }

  const initial = profile?.full_name?.trim().charAt(0).toUpperCase() ?? '?';

  return (
    <div ref={ref} className="relative">
      {variant === 'quiet' ? (
        <button
          onClick={() => { setOpen((o) => !o); setRoleSubmenu(false); setThemeSubmenu(false); }}
          className="flex items-center gap-[9px] w-full text-left cursor-pointer bg-transparent"
        >
          <div
            className="w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0 overflow-hidden font-serif text-[13px]"
            style={{ background: 'var(--avatar-bg)', color: 'var(--avatar-text)' }}
          >
            {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[12.5px] text-app-fg truncate">{profile?.full_name ?? 'User'}</p>
            <p className="text-[10.5px] text-app-muted">{ROLE_LABELS[activeRole]}</p>
          </div>
        </button>
      ) : (
      <button
        onClick={() => { setOpen((o) => !o); setRoleSubmenu(false); setThemeSubmenu(false); }}
        title={collapsed ? (profile?.full_name ?? 'User') : undefined}
        className={`flex items-center gap-3 w-full px-3 py-3 hover:bg-black/5 rounded-xl transition-colors text-left ${collapsed ? 'md:justify-center md:px-0' : ''}`}
      >
        <div className="w-8 h-8 rounded-full avatar-host flex items-center justify-center text-cream text-sm font-semibold shrink-0 overflow-hidden">
          {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
        </div>
        <div className={`flex-1 min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
          <p className="text-sm font-medium text-app-fg truncate">{profile?.full_name ?? 'User'}</p>
          <p className="text-xs text-app-muted">{ROLE_LABELS[activeRole]}</p>
        </div>
      </button>
      )}

      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-56 bg-app-card border border-app-border rounded-2xl shadow-2xl z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-app-border flex items-center gap-3">
            <div className="w-9 h-9 rounded-full avatar-host flex items-center justify-center text-cream text-sm font-semibold shrink-0 overflow-hidden">
              {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-app-fg truncate">{profile?.full_name ?? 'User'}</p>
              <p className="text-xs text-app-muted">{ROLE_LABELS[activeRole]}</p>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1.5">
            <button
              onClick={handleSettings}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-app-fg-2 hover:text-app-fg hover:bg-black/5 transition-colors text-left"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 text-app-muted">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
              </svg>
              Profile Settings
            </button>

            {/* Switch Role — expands inline (not a side flyout) so it never
                overflows the narrow mobile drawer this menu lives in. */}
            {roles.length > 1 && (
              <div>
                <button
                  onClick={() => { setRoleSubmenu((s) => !s); setThemeSubmenu(false); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-app-fg-2 hover:text-app-fg hover:bg-black/5 transition-colors text-left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 text-app-muted">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z" />
                  </svg>
                  Switch Role
                  <ChevronRight className={roleSubmenu ? 'rotate-90' : undefined} />
                </button>
                {roleSubmenu && (
                  <div className="bg-app-bg mx-2 mb-1 rounded-xl overflow-hidden">
                    {roles.map((r) => (
                      <button
                        key={r}
                        onClick={() => void handleSwitch(r)}
                        className={`flex items-center justify-between w-full pl-9 pr-4 py-2.5 text-sm transition-colors text-left ${
                          r === activeRole ? 'text-app-fg bg-black/10' : 'text-app-fg-2 hover:text-app-fg hover:bg-black/5'
                        }`}
                      >
                        {ROLE_LABELS[r]}
                        {r === activeRole && <Check />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Theme */}
            <div>
              <button
                onClick={() => { setThemeSubmenu((s) => !s); setRoleSubmenu(false); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-app-fg-2 hover:text-app-fg hover:bg-black/5 transition-colors text-left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 text-app-muted">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
                Theme
                <ChevronRight className={themeSubmenu ? 'rotate-90' : undefined} />
              </button>
              {themeSubmenu && (
                <div className="bg-app-bg mx-2 mb-1 rounded-xl overflow-hidden">
                  {(['light', 'dark'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => { setTheme(t); setOpen(false); setThemeSubmenu(false); }}
                      className={`flex items-center justify-between w-full pl-9 pr-4 py-2.5 text-sm transition-colors text-left capitalize ${
                        currentTheme === t ? 'text-app-fg bg-black/10' : 'text-app-fg-2 hover:text-app-fg hover:bg-black/5'
                      }`}
                    >
                      {t}
                      {currentTheme === t && <Check />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-app-border mt-1 pt-1">
              <button
                onClick={() => void handleSignOut()}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-app-fg-2 hover:text-app-fg hover:bg-black/5 transition-colors text-left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 text-app-muted">
                  <path fillRule="evenodd" d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M19 10a.75.75 0 00-.75-.75H8.704l1.048-1.08a.75.75 0 10-1.004-1.114l-2.5 2.571a.75.75 0 000 1.086l2.5 2.572a.75.75 0 101.004-1.115l-1.048-1.08H18.25A.75.75 0 0019 10z" clipRule="evenodd" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountMenu;
